import ClassNames from "classnames";
import { FunctionComponent, useEffect, useRef } from "react";
import styles from "../stylesheets/components/header-bar.module.css";
import mainMenuStyles from "../stylesheets/components/main-menu.module.css";
import {
  Accommodation,
  ActionLinks,
  ColorScheme,
  Language,
  MainMenuItem,
  PageState,
  Picture,
  ThreeSizes,
  TopHeaderVariant,
} from "../types/index.js";
import ActionModuleNavigation from "./ActionModuleNavigation.js";
import ContactNav from "./ContactNav.js";
import HeaderLogo from "./HeaderLogo.js";
import LanguageMenu from "./LanguageMenu.js";
import PriorityNavigation from "./PriorityNavigation.js";

interface Props {
  accommodation: Accommodation | undefined;
  actionLinks: ActionLinks;
  activePagePath: string[];
  hasHeaderImage: boolean;
  isPreview: boolean;
  languageId: Language;
  topHeaderVariant: TopHeaderVariant;
  logo: Picture;
  mainPageURL: string | undefined;
  pages: PageState;
  scheme: ColorScheme;
  topLevelMainMenuItems: MainMenuItem[];
  isStaticHeader: boolean;
  fallbackLanguageId: Language | undefined;
  pageId: string;
  onRef?: (el: HTMLDivElement) => void;
  onIntersectionTriggerRef: (el: HTMLElement) => void;
  logoSize: ThreeSizes;
}

const HeaderBar: FunctionComponent<Props> = ({
  accommodation,
  actionLinks,
  activePagePath,
  hasHeaderImage,
  isPreview,
  languageId,
  topHeaderVariant,
  logo,
  logoSize,
  mainPageURL,
  pages,
  scheme,
  topLevelMainMenuItems,
  isStaticHeader,
  fallbackLanguageId,
  pageId,
  onRef,
  onIntersectionTriggerRef,
}) => {
  const topBlockElRef = useRef<HTMLDivElement>(null);
  const blockCElRef = useRef<HTMLDivElement>(null);
  const isInHeaderNav = topHeaderVariant === "in-header-nav";
  const isBelowHeaderNav = topHeaderVariant === "below-header-nav";

  const handleRef = () => {
    if (!topBlockElRef.current) return;
    isBelowHeaderNav && onIntersectionTriggerRef(topBlockElRef.current);
    onRef && onRef(topBlockElRef.current);
  };

  const handleBlockCRef = () => {
    blockCElRef.current &&
      isInHeaderNav &&
      onIntersectionTriggerRef(blockCElRef.current);
  };

  useEffect(() => {
    handleRef();
    handleBlockCRef();
  }, [topHeaderVariant]);

  const languageMenu = (
    <LanguageMenu
      className={ClassNames(
        styles.HeaderBar__LanguageMenu,
        styles["HeaderBar__LanguageMenu--all-in-header"],
      )}
      isPreview={isPreview}
      languageId={languageId}
      scheme={scheme}
      pageId={pageId}
      variant="horizontal"
    />
  );

  return (
    <div
      ref={topBlockElRef}
      className={ClassNames(styles.HeaderBar, {
        [styles["HeaderBar--top-below-header-nav"]]: isBelowHeaderNav,
        [styles["HeaderBar--top-in-header-nav"]]: isInHeaderNav,
      })}
    >
      <div className={styles.HeaderBar__Language_and_MainMenu}>
        {isStaticHeader && languageMenu}
      </div>

      <HeaderLogo
        isPreview={isPreview}
        logo={logo}
        mainPageURL={mainPageURL}
        scheme={scheme}
        maxSize={{ width: 250, height: 120 }}
        size={logoSize}
        className={styles.HeaderBar__HeaderLogo}
        imageClassName={styles.HeaderBar__HeaderLogoImage}
      />

      <div className={styles.HeaderBar__BlockF}>
        <ActionModuleNavigation
          className={styles["HeaderBar__ActionModuleNavigation--block"]}
          isPreview={isPreview}
          actionLinks={actionLinks}
          scheme={scheme}
          showQuickLinks={true}
        />
        {isStaticHeader && (
          <div className={styles.HeaderBar__BlockC} ref={blockCElRef}>
            {languageMenu}
            <ContactNav
              className={ClassNames(styles.HeaderBar__Contact, "Nav")}
              accommodation={accommodation}
              actionLinks={actionLinks}
              languageId={languageId}
            />
          </div>
        )}
        {isInHeaderNav && (
          <div className={styles.HeaderBar__BlockA}>
            <PriorityNavigation
              className={mainMenuStyles["MainMenu__Wrapper--horizontal"]}
              isPreview={isPreview}
              languageId={languageId}
              menuItems={topLevelMainMenuItems}
              pages={pages}
              scheme={scheme}
              topHeaderVariant={topHeaderVariant}
              activePagePath={activePagePath}
              hasHeaderImage={hasHeaderImage}
              absolutePositioning={false}
              fallbackLanguageId={fallbackLanguageId}
            />

            <ActionModuleNavigation
              isPreview={isPreview}
              actionLinks={actionLinks}
              scheme={scheme}
              showQuickLinks={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderBar;
